import React, { useState } from "react";
import { Form, Button, Row, Col, Alert } from "react-bootstrap";
import OuterBodyModal from "../../../components/ui/OuterBodyModal";
import axios from "axios";
import { BASE_URL } from "../../../env";

const DeveloperDeleteModal = ({
  showModal,
  handleClose,
  onDeleteSuccess,
  memberId,
}) => {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const resetModal = () => {
    setStep(1);
    setReason("");
    setUsername("");
    setPassword("");
    setError("");
    setLoading(false);
  };

  const handleModalClose = () => {
    resetModal();
    handleClose();
  };

  const handleNextStep = () => {
    if (step === 2 && reason.trim().length === 0) {
      setError("Please provide a reason for deletion.");
      return;
    }
    setError("");
    setStep(step + 1);
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Step 1: Verify Credentials
      const loginRes = await axios.post(`${BASE_URL}/api/superadmin/login`, {
        username,
        password,
      });

      if (loginRes.data.userType !== "developer") {
        setError("Only a developer can authorize this deletion.");
        setLoading(false);
        return;
      }

      // Step 2: Delete Member
      const deleteRes = await axios.post(`${BASE_URL}/api/delMember`, {
        memberId,
        reason, // Optional: backend could log this
        authorizedBy: username
      });

      if (deleteRes.data.message) {
        onDeleteSuccess();
        setStep(4);
      } else {
        setError(deleteRes.data.error || "Failed to delete member.");
      }
    } catch (err) {
      console.error(err);
      const serverError = err.response?.data?.error;
      const serverDetails = err.response?.data?.details;
      setError(serverDetails ? `${serverError}: ${serverDetails}` : (serverError || "Invalid credentials or server error."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <OuterBodyModal
      modalTitle="Authorized Administrative Deletion"
      showModal={showModal}
      handleClose={handleModalClose}
    >
      <div className="p-3">
        {error && <Alert variant="danger">{error}</Alert>}

        {step === 1 && (
          <div className="text-center">
            <h5 className="mb-4">Are you absolutely sure you want to delete this member?</h5>
            <p className="text-muted mb-4">This action is permanent and cannot be undone. All data and files will be removed.</p>
            <div className="d-flex justify-content-center gap-3">
              <Button variant="danger" size="lg" onClick={handleNextStep} style={{ minWidth: '120px' }}>
                Yes, Delete
              </Button>
              <Button variant="secondary" size="lg" onClick={handleModalClose} style={{ minWidth: '120px' }}>
                No, Cancel
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h5 className="mb-3">Reason for Deletion</h5>
            <Form.Group className="mb-4">
              <Form.Label>Please provide a detailed reason (Max 600 characters)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                maxLength={600}
                placeholder="Why is this record being deleted?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                autoFocus
              />
              <div className="text-end text-muted mt-1 small">
                {reason.length}/600 characters
              </div>
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setStep(1)}>Back</Button>
              <Button variant="primary" onClick={handleNextStep}>Next</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <Form onSubmit={handleFinalSubmit}>
            <h5 className="mb-3">Developer Authentication</h5>
            
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Developer Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Developer Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setStep(2)} disabled={loading}>Back</Button>
              <Button 
                variant="danger" 
                type="submit" 
                disabled={loading}
              >
                {loading ? "Deleting..." : "Confirm & Delete Everything"}
              </Button>
            </div>
          </Form>
        )}

        {step === 4 && (
          <div className="text-center py-4">
            <div className="mb-4">
              <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>
              <h2 className="text-success mt-3">Successfully Deleted!</h2>
            </div>
            <p className="mb-4 text-muted">The record and all associated files have been permanently removed from the system.</p>
            <Button variant="success" size="lg" onClick={handleModalClose} style={{ minWidth: '150px' }}>
              Done
            </Button>
          </div>
        )}
      </div>
    </OuterBodyModal>
  );
};

export default DeveloperDeleteModal;
